import { forwardRef } from "react";

import { formatPhoneInput, phoneDigitsAndPlus } from "@/lib/phoneFormat";

interface PhoneInputProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "value" | "onChange" | "type" | "inputMode"
  > {
  /** Raw value (whatever shape — phoneDigitsAndPlus + formatting
   *  happen on the way in/out). Parents can store either the raw
   *  user input or an E.164 string; both render correctly. */
  value: string;
  /** Called with the formatted display string. Use this directly
   *  for controlled state — the live-mask runs inside the input
   *  so what the user sees IS what's in state. The server side
   *  normalizes via :func:`pencilout.api.services.sms_verify.normalize_e164`,
   *  so submitting ``"(704) 555-1234"`` works fine. */
  onChange: (next: string) => void;
}

/** Reusable phone input with live US-friendly masking.
 *
 *  Renders ``<input type="tel">`` with ``inputMode="tel"`` so iOS
 *  gives the right keyboard. Formats progressively as the user
 *  types: ``7045551234`` → ``(704) 555-1234`` while they type, with
 *  partial states for incomplete numbers (``704`` → ``(704``,
 *  ``704555`` → ``(704) 555``, etc).
 *
 *  Submission shape: server endpoints already accept loose input
 *  (``services/sms_verify.normalize_e164``), so the formatted
 *  display string is fine to POST as-is. No need to strip the mask
 *  before submit.
 *
 *  International inputs (anything starting with ``+`` other than
 *  ``+1``) pass through unmasked — country-specific formatting is
 *  out of scope; the server still normalizes. */
export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onChange, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      type="tel"
      inputMode="tel"
      autoComplete="tel"
      value={value}
      onChange={(e) => onChange(formatPhoneInput(e.target.value))}
      // ``onPaste`` re-runs the formatter — pasting
      // ``704.555.1234`` from a contact card lands as ``(704) 555-1234``.
      onPaste={(e) => {
        const text = e.clipboardData.getData("text");
        if (!text) return;
        e.preventDefault();
        onChange(formatPhoneInput(text));
      }}
      {...rest}
    />
  );
});

/** Validate that a phone has enough digits to be plausibly real.
 *  Used by forms that need a "submit disabled until digit count
 *  reasonable" check without committing to full E.164 parsing. */
export function isPhoneInputComplete(value: string): boolean {
  const digits = phoneDigitsAndPlus(value).replace(/\+/, "");
  return digits.length >= 10;
}
